import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import type { JSX } from "react";
import { publicApi } from "../shared/api";
import { getBasePath } from "../shared/base-path";
import { getPendingHttpRequests, HTTP_LOADING_EVENT } from "../shared/http";
import { ranges, StatusBars } from "../shared/uptime-ui";
import type { ComponentResponse, IncidentResponse, SummaryResponse, UptimeBucket } from "../shared/types";
import "./styles.css";

function fmtDate(value?: string): string {
  if (!value) return "";
  const d = new Date(value);
  return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
}

function fmtStatus(status: string): string {
  return status.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusIndicatorColor(indicator: string): string {
  switch (indicator) {
    case "none":
      return "var(--status-operational)";
    case "minor":
      return "var(--status-degraded)";
    case "major":
      return "var(--status-partial-outage)";
    default:
      return "var(--status-major-outage)";
  }
}

function TerminalLoader(): JSX.Element {
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let frame = 0;
    const id = window.setInterval(() => {
      frame++;
      setProgress(Math.min(100, Math.floor(frame * 3.5 + Math.random() * 8)));
    }, 60);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (progress >= 100 && !done) setDone(true);
  }, [progress, done]);

  if (done) return <></>;

  const barWidth = 40;
  const filled = Math.round((progress / 100) * barWidth);
  const bar = "█".repeat(filled) + "░".repeat(barWidth - filled);

  return (
    <div className="terminal-loader">
      <pre className="ascii-logo">{`███████╗██╗  ██╗ █████╗ ██████╗ ██╗  ██╗
██╔════╝██║  ██║██╔══██╗██╔══██╗██║ ██╔╝
███████╗███████║███████║██████╔╝█████╔╝${" "}
╚════██║██╔══██║██╔══██║██╔══██╗██╔═██╗${" "}
███████║██║  ██║██║  ██║██║  ██║██║  ██╗
╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝`}</pre>
      <div className="loader-bar">
        <span style={{ color: "var(--status-operational)" }}>{bar}</span> {progress}%
      </div>
      <div className="loader-text">Loading status data...</div>
    </div>
  );
}

function PublicHomePage(): JSX.Element {
  const queryRange = new URLSearchParams(window.location.search).get("range");
  const initialRange = queryRange && (ranges as readonly string[]).includes(queryRange) ? queryRange : "14d";
  const [range, setRange] = useState(initialRange);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [uptime, setUptime] = useState<Record<string, UptimeBucket[]>>({});
  const [groupUptime, setGroupUptime] = useState<Record<string, UptimeBucket[]>>({});
  const [history, setHistory] = useState<IncidentResponse[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [networkLoading, setNetworkLoading] = useState<boolean>(getPendingHttpRequests() > 0);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const onLoading = (event: Event): void => {
      const custom = event as CustomEvent<{ pending?: number }>;
      setNetworkLoading((custom.detail?.pending ?? 0) > 0);
    };
    window.addEventListener(HTTP_LOADING_EVENT, onLoading as EventListener);
    return () => window.removeEventListener(HTTP_LOADING_EVENT, onLoading as EventListener);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [summaryData, uptimeData, groupUptimeData, incidentsData] = await Promise.all([
          publicApi.summary(),
          publicApi.uptime(range),
          publicApi.groupUptime(range),
          // Past Incidents is the history; without it the page still shows today's status.
          publicApi.incidents().catch(() => ({ incidents: [] })),
        ]);
        if (cancelled) return;
        setSummary(summaryData);
        setUptime(uptimeData);
        setGroupUptime(groupUptimeData);
        setHistory(incidentsData.incidents ?? []);
        setError("");
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range]);

  useEffect(() => {
    if (summary?.page?.name) {
      document.title = summary.page.name;
    }
  }, [summary?.page?.name]);

  const groups = useMemo(() => {
    const result: Array<ComponentResponse & { children: ComponentResponse[] }> = [];
    const components = summary?.components ?? [];
    const summaryGroups = summary?.groups ?? [];
    const childrenByGroup = new Map<string, ComponentResponse[]>();
    for (const c of components) {
      if (c.group_id) {
        const children = childrenByGroup.get(c.group_id) ?? [];
        children.push(c);
        childrenByGroup.set(c.group_id, children);
      }
    }
    for (const g of summaryGroups) {
      result.push({ ...g, children: childrenByGroup.get(g.id) ?? [] });
    }
    return result;
  }, [summary]);

  const active = summary?.incidents ?? [];
  const past = history.filter((it) => it.status === "resolved" || it.status === "postmortem");
  const statusCounts = summary
    ? [`${summary.status.operational_components} operational`, `${summary.status.degraded_components} degraded`].join(" - ")
    : "";

  if (loading && !summary) {
    return (
      <div style={{ maxWidth: "80ch", margin: "0 auto", padding: "1lh 2ch" }}>
        <TerminalLoader />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "80ch", margin: "0 auto", padding: "1lh 2ch" }}>
      <pre className="ascii-logo ascii-logo-header">{`███████╗██╗  ██╗ █████╗ ██████╗ ██╗  ██╗
██╔════╝██║  ██║██╔══██╗██╔══██╗██║ ██╔╝
███████╗███████║███████║██████╔╝█████╔╝${" "}
╚════██║██╔══██║██╔══██║██╔══██╗██╔═██╗${" "}
███████║██║  ██║██║  ██║██║  ██║██║  ██╗
╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝`}</pre>
      <div style={{ textAlign: "center", color: "var(--foreground2)", marginBottom: "1lh" }}>
        {summary?.page.description && <p>{summary.page.description}</p>}
        <p style={{ fontSize: "0.85em", marginTop: "0.25lh" }}>
          {summary?.page.updated_at && <>Last updated: {fmtDate(summary.page.updated_at)} · </>}
          <a href={`${getBasePath()}/api/v1/summary`}>API</a> · <a href={`${getBasePath()}/docs/index.html`}>OpenAPI</a>
        </p>
      </div>
      {summary && (
        <div
          style={{
            marginBottom: "1lh",
            padding: "0.5lh 2ch",
            textAlign: "center",
            backgroundColor: statusIndicatorColor(summary.status.indicator),
            color: summary.status.indicator === "minor" ? "#78350f" : "white",
          }}
        >
          <div style={{ fontWeight: 600 }}>{summary.status.description}</div>
          <div style={{ marginTop: "0.25lh", fontSize: "0.85em" }}>{statusCounts}</div>
        </div>
      )}
      <div style={{ marginBottom: "1lh", display: "flex", justifyContent: "flex-end", gap: "1ch" }}>
        {ranges.map((item) => (
          <a
            key={item}
            href={`${getBasePath()}/?range=${item}`}
            onClick={(e) => {
              e.preventDefault();
              setRange(item);
              const url = new URL(window.location.href);
              url.searchParams.set("range", item);
              window.history.replaceState(null, "", url.toString());
            }}
          >
            <button
              variant-={item === range ? "foreground0" : "background2"}
              size-="small"
            >
              {item}
            </button>
          </a>
        ))}
      </div>

      {error && (
        <div box-="round" style={{ color: "var(--status-major-outage)", borderColor: "var(--status-major-outage)", marginBottom: "1lh" }}>
          Failed to load status data: {error}
        </div>
      )}

      {active.length > 0 && (
        <section style={{ marginBottom: "1lh" }}>
          <h2>Active Incidents</h2>
          {active.map((incident) => (
            <article box-="round" style={{ marginBottom: "0.5lh" }} key={incident.id} id={`incident-${incident.id}`}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "2ch" }}>
                <a style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} href={`${getBasePath()}/incidents/${incident.id}`}>{incident.name}</a>
                <span data-status-badge={incident.status}>{fmtStatus(incident.status)}</span>
              </div>
              <div style={{ marginTop: "0.25lh", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.85em", color: "var(--foreground2)" }}>
                <span>Impact: <strong style={{ textTransform: "capitalize" }}>{incident.impact}</strong></span>
                <span>{fmtDate(incident.started_at)}</span>
              </div>
              {incident.body && (
                <p style={{ marginTop: "0.5lh", fontSize: "0.9em" }}>{incident.body}</p>
              )}
              {incident.components && incident.components.length > 0 && (
                <div style={{ marginTop: "0.5lh", fontSize: "0.85em", color: "var(--foreground2)" }}>
                  Affected: {incident.components.map((c) => c.name).join(", ")}
                </div>
              )}
              {incident.incident_updates && incident.incident_updates.length > 0 && (
                <div style={{ marginTop: "0.5lh", borderTop: "1px solid var(--background2)", paddingTop: "0.5lh" }}>
                  {incident.incident_updates.map((update) => (
                    <div key={update.id} style={{ marginBottom: "0.5lh" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "1ch", fontSize: "0.85em" }}>
                        <span data-status-badge={update.status}>{fmtStatus(update.status)}</span>
                        <span style={{ color: "var(--foreground2)" }}>{fmtDate(update.created_at)}</span>
                      </div>
                      {update.body && <p style={{ marginTop: "0.25lh", fontSize: "0.9em" }}>{update.body}</p>}
                    </div>
                  ))}
                </div>
              )}
            </article>
          ))}
        </section>
      )}

      {groups.map((group) => (
        <section key={group.id} box-="round" style={{ marginBottom: "1lh" }}>
          <div
            role="button"
            tabIndex={0}
            aria-expanded={expandedGroups.has(group.id)}
            style={{ display: "flex", cursor: "pointer", alignItems: "center", justifyContent: "space-between", gap: "2ch" }}
            onClick={() =>
              setExpandedGroups((prev) => {
                const next = new Set(prev);
                if (next.has(group.id)) next.delete(group.id);
                else next.add(group.id);
                return next;
              })
            }
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              setExpandedGroups((prev) => {
                const next = new Set(prev);
                if (next.has(group.id)) next.delete(group.id);
                else next.add(group.id);
                return next;
              });
            }}
          >
            <div style={{ display: "flex", minWidth: 0, alignItems: "center", gap: "1ch" }}>
              <span aria-hidden style={{ flexShrink: 0, color: "var(--foreground2)" }}>
                {expandedGroups.has(group.id) ? "[-]" : "[+]"}
              </span>
              {group.logo && <img src={group.logo} alt="" style={{ height: "1.25em", width: "1.25em", flexShrink: 0, objectFit: "contain" }} loading="lazy" />}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.name}</div>
                <div style={{ fontSize: "0.85em", color: "var(--foreground2)" }}>
                  {group.children.length} {group.children.length === 1 ? "component" : "components"}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "1ch" }}>
              <span data-status-badge={group.status}>
                {fmtStatus(group.status)}
              </span>
            </div>
          </div>
          <StatusBars id={group.id} range={range} buckets={groupUptime[group.id] ?? []} />
          {expandedGroups.has(group.id) && (
            <div style={{ marginTop: "0.5lh" }}>
              <div is-="separator" style={{ marginBottom: "0.5lh" }} />
              {group.children.map((comp, idx) => (
                <div key={comp.id} style={{ paddingTop: idx > 0 ? "0.5lh" : 0, marginTop: idx > 0 ? "0.5lh" : 0, borderTop: idx > 0 ? "1px solid var(--background2)" : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "2ch" }}>
                    <span style={{ display: "flex", minWidth: 0, alignItems: "center", gap: "1ch" }}>
                      {comp.logo && <img src={comp.logo} alt="" style={{ height: "1em", width: "1em", flexShrink: 0, objectFit: "contain" }} loading="lazy" />}
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{comp.name}</span>
                    </span>
                    <span data-status-badge={comp.status}>
                      {fmtStatus(comp.status)}
                    </span>
                  </div>
                  <StatusBars id={comp.id} range={range} buckets={uptime[comp.id] ?? []} />
                </div>
              ))}
              {group.children.length === 0 && <p style={{ color: "var(--foreground2)" }}>No components in this group.</p>}
            </div>
          )}
        </section>
      ))}

      <Incidents title="Past Incidents" items={past} />
    </div>
  );
}

function Incidents({ title, items }: { title: string; items: IncidentResponse[] }): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <section style={{ marginTop: "1lh" }}>
      <h2>{title}</h2>
      {items.map((incident) => (
        <article box-="round" style={{ marginBottom: "0.5lh" }} key={incident.id} id={`incident-${incident.id}`}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "2ch" }}>
            <a style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} href={`${getBasePath()}/incidents/${incident.id}`}>{incident.name}</a>
            <span style={{ flexShrink: 0, fontSize: "0.85em", color: "var(--foreground2)" }}>{fmtDate(incident.started_at)}</span>
          </div>
          <div style={{ marginTop: "0.25lh", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.85em", color: "var(--foreground2)" }}>
            <span>{fmtStatus(incident.status)}</span>
            <span style={{ fontWeight: 500, textTransform: "capitalize" }}>{incident.impact}</span>
          </div>
        </article>
      ))}
    </section>
  );
}

function PublicIncidentPage({ incidentID }: { incidentID: string }): JSX.Element {
  const [incident, setIncident] = useState<IncidentResponse | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await publicApi.incidentDetail(incidentID);
        setIncident(data.incident);
      } catch (err) {
        setError((err as Error).message);
      }
    })();
  }, [incidentID]);

  return (
    <div style={{ maxWidth: "65ch", margin: "0 auto", padding: "1lh 2ch" }}>
      <p style={{ marginBottom: "0.5lh" }}>
        <a href={`${getBasePath()}/`}>Back to status</a>
      </p>
      {error && (
        <div box-="round" style={{ marginBottom: "0.5lh", color: "var(--status-major-outage)" }}>
          {error}
        </div>
      )}
      {!incident && !error && (
        <div box-="round" style={{ color: "var(--foreground2)" }}>
          Loading incident...
        </div>
      )}
      {incident && (
        <>
          <div box-="round" style={{ marginBottom: "1lh" }}>
            <h1 style={{ marginBottom: "0.5lh" }}>{incident.name}</h1>
            <p>
              <strong>Status:</strong> {fmtStatus(incident.status)}
            </p>
            <p>
              <strong>Started:</strong> {fmtDate(incident.started_at)}
            </p>
            {incident.resolved_at && (
              <p>
                <strong>Resolved:</strong> {fmtDate(incident.resolved_at)}
              </p>
            )}
            {incident.body && <p style={{ marginTop: "0.5lh" }}>{incident.body}</p>}
          </div>
          <h2 style={{ marginBottom: "0.5lh" }}>Updates</h2>
          {(incident.incident_updates ?? []).map((update) => (
            <div box-="round" style={{ marginBottom: "0.5lh" }} key={update.id}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "2ch" }}>
                <strong>{fmtStatus(update.status)}</strong>
                <span style={{ fontSize: "0.85em", color: "var(--foreground2)" }}>{fmtDate(update.created_at)}</span>
              </div>
              <p style={{ marginTop: "0.5lh" }}>{update.body}</p>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function App(): JSX.Element {
  const base = getBasePath();
  const path = base ? (window.location.pathname.replace(base, "") || "/") : window.location.pathname;
  const incidentMatch = path.match(/^\/incidents\/([^/]+)$/);
  if (incidentMatch) {
    return <PublicIncidentPage incidentID={incidentMatch[1]} />;
  }
  return <PublicHomePage />;
}

const root = document.getElementById("root");
if (root) {
  createRoot(root).render(<App />);
}
